import React, { useEffect, useState } from "react";
import {
  Card, Row, Col, Button, Tag, Space, Select, Input, Modal, Form, message,
  Drawer, Empty, Tabs, Popconfirm, Tooltip, Typography,
} from "antd";
import { PlusOutlined, ReloadOutlined, DeleteOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import {
  getNotes, getNote, createNote, patchNote, deleteNote, addNoteComment,
  getMyNotebook, addPersonalNote, deletePersonalNote, getMentionableUsers,
  decideAttendanceChangeRequest } from "../api/client";
import { useAuth } from "../context/AuthContext";

const { Text } = Typography;
import { toJalali } from "../utils/dateUtils";

const MODULES = [
  { v: "general", l: "عمومی" }, { v: "supply_chain", l: "زنجیره تأمین" },
  { v: "production", l: "تولید" }, { v: "payroll", l: "حقوق" },
  { v: "accounting", l: "حسابداری" }, { v: "dashboard", l: "داشبورد" }, { v: "report", l: "گزارش" },
];
const mLabel = (v) => MODULES.find((m) => m.v === v)?.l || v;
const PRIO = { low: { l: "کم", c: "default" }, normal: { l: "عادی", c: "blue" }, high: { l: "زیاد", c: "orange" }, urgent: { l: "فوری", c: "red" } };
const STAT = { open: { l: "باز", c: "blue" }, in_review: { l: "در بررسی", c: "gold" }, accepted: { l: "پذیرفته", c: "green" }, rejected: { l: "رد", c: "red" }, done: { l: "انجام‌شده", c: "green" }, archived: { l: "بایگانی", c: "default" } };

export function NotesBoard() {
  const { can, user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const [sp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ module: sp.get("module") || undefined, status: undefined });
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [users, setUsers] = useState([]);
  const [form] = Form.useForm();

  const load = () => getNotes(filters).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [JSON.stringify(filters)]);
  useEffect(() => { getMentionableUsers().then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const userOpts = users.map((u) => ({ value: u.username, label: `${u.full_name} — @${u.username}` }));

  const create = async () => {
    const v = await form.validateFields();
    try {
      await createNote({ ...v, mentioned_users: v.mentioned || [] });
      message.success("یادداشت ثبت شد"); setOpen(false); form.resetFields(); load();
    } catch (e) { if (e?.errorFields) return; message.error(e?.response?.data?.detail || "خطا"); }
  };
  const openDetail = async (id) => { try { const r = await getNote(id); setDetail(r.data); setComment(""); } catch { message.error("خطا"); } };
  const decide = async (approve) => {
    try {
      const r = await decideAttendanceChangeRequest(detail.id, approve);
      message.success(r.data?.message || "انجام شد"); openDetail(detail.id); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      message.error(typeof d === "string" ? d : (d?.message || "خطا"));
    }
  };
  const setStatus = async (st) => { await patchNote(detail.id, { status: st }); message.success("به‌روزرسانی شد"); openDetail(detail.id); load(); };
  const sendComment = async () => { if (!comment.trim()) return; await addNoteComment(detail.id, comment); setComment(""); openDetail(detail.id); };
  const remove = async (id) => { try { await deleteNote(id); message.success("حذف شد"); setDetail(null); load(); } catch (e) { message.error(e?.response?.data?.detail || "خطا"); } };

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Select allowClear placeholder="ماژول" style={{ width: 150 }} value={filters.module}
          onChange={(v) => setFilters((f) => ({ ...f, module: v }))} options={MODULES.map((m) => ({ value: m.v, label: m.l }))} />
        <Select allowClear placeholder="وضعیت" style={{ width: 140 }} value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          options={Object.entries(STAT).map(([v, o]) => ({ value: v, label: o.l }))} />
        <Button icon={<ReloadOutlined />} onClick={load}>تازه‌سازی</Button>
        {can("notes.create") && <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setOpen(true); }}>یادداشتِ جدید</Button>}
      </Space>

      {rows.length ? (
        <Row gutter={[12, 12]}>
          {rows.map((n) => (
            <Col xs={24} md={12} lg={8} key={n.id}>
              <Card size="small" hoverable onClick={() => openDetail(n.id)}
                title={<Space><Tag color={PRIO[n.priority]?.c}>{PRIO[n.priority]?.l}</Tag><b>{n.title || "بدونِ عنوان"}</b></Space>}
                extra={<Tag color={STAT[n.status]?.c}>{STAT[n.status]?.l}</Tag>}>
                <div style={{ color: "#555", minHeight: 36 }}>{(n.body || "").slice(0, 120)}</div>
                <div style={{ marginTop: 6, fontSize: 12, color: "#8c8c8c", display: "flex", justifyContent: "space-between" }}>
                  <Tag>{mLabel(n.module_context)}</Tag>
                  <span>{n.assigned_to ? `واگذار: ${n.assigned_to}` : ""}</span>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      ) : <Empty description="یادداشتی نیست" />}

      <Modal title="یادداشتِ جدید" open={open} onOk={create} onCancel={() => setOpen(false)} okText="ثبت" cancelText="انصراف" destroyOnHidden>
        <Form form={form} layout="vertical" style={{ direction: "rtl" }}>
          <Form.Item name="title" label="عنوان"><Input /></Form.Item>
          <Form.Item name="body" label="متن" rules={[{ required: true, message: "متن لازم است" }]}><Input.TextArea rows={3} /></Form.Item>
          <Row gutter={10}>
            <Col xs={12}><Form.Item name="module_context" label="ماژول" initialValue="general"><Select options={MODULES.map((m) => ({ value: m.v, label: m.l }))} /></Form.Item></Col>
            <Col xs={12}><Form.Item name="priority" label="اولویت" initialValue="normal"><Select options={Object.entries(PRIO).map(([v, o]) => ({ value: v, label: o.l }))} /></Form.Item></Col>
          </Row>
          <Row gutter={10}>
            <Col xs={12}><Form.Item name="assigned_to" label="واگذاری به">
              <Select showSearch allowClear placeholder="یک نفر را انتخاب کنید" optionFilterProp="label" options={userOpts} />
            </Form.Item></Col>
            <Col xs={12}><Form.Item name="mentioned" label="منشن (@)">
              <Select mode="multiple" showSearch allowClear placeholder="جست‌وجو و انتخابِ افراد" optionFilterProp="label" options={userOpts} maxTagCount="responsive" />
            </Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      <Drawer open={!!detail} onClose={() => setDetail(null)} width={520} title={detail?.title || "یادداشت"}>
        {detail && (
          <>
            <Space wrap style={{ marginBottom: 10 }}>
              <Tag color={PRIO[detail.priority]?.c}>{PRIO[detail.priority]?.l}</Tag>
              <Tag>{mLabel(detail.module_context)}</Tag>
              <Tag color={STAT[detail.status]?.c}>{STAT[detail.status]?.l}</Tag>
              {detail.assigned_to && <Tag color="purple">واگذار: {detail.assigned_to}</Tag>}
            </Space>
            <div style={{ whiteSpace: "pre-wrap", marginBottom: 12 }}>{detail.body}</div>
            {/* درخواستِ اصلاحِ حضورِ ماهِ نهایی‌شده (v2.124.0) — مدیرِ اصلی با یک دکمه تأیید و اعمال یا رد می‌کند */}
            {detail.related_entity_type === "attendance_change" && (
              <Space wrap style={{ marginBottom: 12, padding: 10, borderRadius: 8, background: "var(--gx-surface-2, #f6f7fb)", width: "100%" }}>
                {["accepted", "rejected"].includes(detail.status)
                  ? <Tag color={detail.status === "accepted" ? "green" : "red"}>
                      {detail.status === "accepted" ? "تأیید و اعمال شده" : "رد شده"}</Tag>
                  : isAdmin
                    ? <>
                        <Button type="primary" onClick={() => decide(true)}>تأیید و اعمال</Button>
                        <Button danger onClick={() => decide(false)}>رد</Button>
                      </>
                    : <Text type="secondary">در انتظارِ تأییدِ مدیرِ اصلی</Text>}
              </Space>
            )}
            <Space wrap style={{ marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: "#888" }}>تغییرِ وضعیت:</span>
              {Object.entries(STAT).map(([v, o]) => <Button key={v} size="small" onClick={() => setStatus(v)}>{o.l}</Button>)}
              <Popconfirm title="حذف شود؟" onConfirm={() => remove(detail.id)} okText="بله" cancelText="خیر">
                <Tooltip title="حذف"><Button size="small" danger icon={<DeleteOutlined />} /></Tooltip>
              </Popconfirm>
            </Space>
            <div style={{ borderTop: "1px solid #eee", paddingTop: 10 }}>
              <b style={{ fontSize: 13 }}>گفت‌وگو</b>
              {(detail.comments || []).map((c) => (
                <div key={c.id} style={{ background: "var(--bg-subtle,#fafafa)", borderRadius: 8, padding: "6px 10px", margin: "6px 0", fontSize: 13 }}>
                  <b>{c.author}</b> · <span style={{ color: "#aaa" }}>{toJalali(c.created_at)}</span>
                  <div>{c.body}</div>
                </div>
              ))}
              <Space.Compact style={{ width: "100%", marginTop: 8 }}>
                <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="پاسخ…" onPressEnter={sendComment} />
                <Button type="primary" onClick={sendComment}>ارسال</Button>
              </Space.Compact>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}

export function Notebook() {
  const [rows, setRows] = useState([]);
  const [form] = Form.useForm();
  const load = () => getMyNotebook().then((r) => setRows(r.data)).catch(() => {});
  useEffect(load, []);
  const add = async () => {
    const v = await form.validateFields();
    await addPersonalNote({ ...v, is_private: true }); form.resetFields(); load(); message.success("ثبت شد");
  };
  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} md={9}>
        <Card size="small" title="یادداشتِ شخصیِ جدید">
          <Form form={form} layout="vertical" style={{ direction: "rtl" }}>
            <Form.Item name="title" label="عنوان"><Input /></Form.Item>
            <Form.Item name="body" label="متن" rules={[{ required: true }]}><Input.TextArea rows={3} /></Form.Item>
            <Button type="primary" onClick={add}>ذخیره (فقط برای خودم)</Button>
          </Form>
        </Card>
      </Col>
      <Col xs={24} md={15}>
        {rows.length ? rows.map((p) => (
          <Card key={p.id} size="small" style={{ marginBottom: 8 }} title={p.title || "—"}
            extra={<Popconfirm title="حذف؟" onConfirm={async () => { await deletePersonalNote(p.id); load(); }} okText="بله" cancelText="خیر"><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>}>
            <div style={{ whiteSpace: "pre-wrap" }}>{p.body}</div>
          </Card>
        )) : <Empty description="دفترچه خالی است" />}
      </Col>
    </Row>
  );
}

export default function NotesPage() {
  return (
    <div>
      <h2 style={{ marginTop: 0 }}>یادداشت‌ها و ارتباطاتِ مدیریتی</h2>
      <Tabs items={[
        { key: "board", label: "یادداشت‌های مدیریتی", children: <NotesBoard /> },
        { key: "notebook", label: "دفترچهٔ یادداشتِ من", children: <Notebook /> },
      ]} />
    </div>
  );
}
